import React, { useContext, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Grid, List, ListItem, ListItemText, ListItemIcon, Paper } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { ActionButton } from '../../buttons/ButtonStyles';
import { CardTypes, ProjectSchemeInterface, BoardSchemeInterface, CollectionInterface } from 'ts-interfaces';
import useEventListener from '../../libs/useEventListener';
import { useEnsureProjects } from '../../hooks/useEnsureProjects';
import { hasAccessToResource } from 'ui/utils/usePermissions';
import { sharingV2Doc } from 'ui/utils/workspace/sharingV2';
import { getActiveDesignFirmId } from 'ui/utils/workspace/useWorkspaceContext';
import { ProjectSchemeCreator } from '../projects_scheme/ProjectSchemeCreator';
import { useProjectSchemas, useProjectSchemaDetails } from '../../global-state/useProjectSchemas';
import { canFileInto } from '../../v2/apis/accessApi';
import { FilingWarning } from '../share_resource/v2/FilingWarning';

const KIND_OF: Partial<Record<CardTypes, 'board' | 'collection' | 'concept'>> = {
  [CardTypes.Boards]: 'board',
  [CardTypes.Collections]: 'collection',
  [CardTypes.Concepts]: 'concept',
};
const OWNER_FIELD = { board: 'user', collection: 'uid', concept: 'owner_id' } as const;

interface AssociatedProjectModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { newProjectId: string | null; oldProjectId: string | null }) => Promise<void>;
  // Boards/collections carry associatedProjectId natively; concept callers
  // pass a shim with associatedProjectId mapped from current.project_id.
  data: BoardSchemeInterface | CollectionInterface | { associatedProjectId?: string | null } | null;
  type: CardTypes;
}

export const AssociatedProjectModal: React.FC<AssociatedProjectModalProps> = ({ 
  open, 
  onClose, 
  onSave, 
  data, 
  type 
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectSchemeInterface | null>(null);
  const [initialProjectId, setInitialProjectId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  
  const {
    permissions, 
    ownedProjects, 
    sharedProjects, 
    isProjectsLoading, 
    currentUser,
  } = useEnsureProjects();

  // Get projects state management functions
  const { projects, setProjects } = useProjectSchemas((state) => ({
    projects: state.projects,
    setProjects: state.setProjects
  }));

  function handleClickOutside(event: MouseEvent) {
    if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowDropdown(false);
    }
  }

  useEventListener('mousedown', handleClickOutside);
  
  useEffect(() => { 
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (isProjectsLoading || !data) return;
    
    // Every caller (boards, collections, concepts, material sheets) passes
    // data carrying associatedProjectId — resolve the current selection from it.
    if (data.associatedProjectId) {
      // Find the project this board is associated with
      const allProjects = [...ownedProjects, ...sharedProjects];
      const associatedProject = allProjects.find(p => p.id === data.associatedProjectId);
      
      if (associatedProject) {
        setSelectedProject(associatedProject);
        setInitialProjectId(associatedProject.id);
      } else {
        setSelectedProject(null);
        setInitialProjectId(null);
      }
    } else {
      setSelectedProject(null);
      setInitialProjectId(null);
    }
  }, [ownedProjects, sharedProjects, data, type, isProjectsLoading]);

  const handleToggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleSelectProject = (project: ProjectSchemeInterface | null) => {
    setSelectedProject(project);
    setShowDropdown(false);
  };

  // Handle creating a new project
  const handleCreateNewProject = () => {
    setShowDropdown(false); // Close the dropdown first
    setShowCreatorModal(true); // Open the creator modal
  };

  // Handle project creation/update from ProjectSchemeCreator
  const handleProjectUpdate = (project: ProjectSchemeInterface, addProject = false) => {
    if (addProject) {
      // Add the new project to the projects list
      useProjectSchemas.getState().addProject(project);
      
      // Select the newly created project
      setSelectedProject(project);
      
      // Sync with backend
      setTimeout(() => {
        useProjectSchemaDetails.getState().syncUserProjects();
      }, 0);
    } else {
      // Update existing project
      const updatedProjects = projects.map(p => p.id === project.id ? project : p);
      setProjects(updatedProjects);
      setTimeout(() => {
        useProjectSchemaDetails.getState().syncUserProjects();
      }, 0);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        newProjectId: selectedProject?.id || null,
        oldProjectId: initialProjectId
      });
    } catch (error) {
      console.error('Error saving project association:', error);
    } finally {
      setSaving(false);
    }
  };

  // Teams sharing: a flagged item lists only the projects it may be filed
  // into (canFile); a Personal item never sees a flagged team's projects.
  const kind = KIND_OF[type] ?? 'board';
  const v2 = sharingV2Doc(data);
  const uid = currentUser?.uid || '';
  const ownsItem = v2 && (data as any)?.[OWNER_FIELD[kind]] === uid;
  // Legacy filing stays in the item's own workspace (PR5's same-workspace
  // rule); an unsaved board takes the workspace its first save stamps.
  const itemFirm = (data as any)?.id ? ((data as any).design_firm_id ?? null) : getActiveDesignFirmId(uid);
  const fileable = (project: ProjectSchemeInterface) =>
    v2
      ? canFileInto(kind, data as any, project as any, uid, permissions)
      : (project.design_firm_id ?? null) === itemFirm;
  const v2Projects = () =>
    [...ownedProjects, ...sharedProjects]
      .filter((p, i, all) => all.findIndex((q) => q.id === p.id) === i)
      .filter((p) => !p.archive && fileable(p));

  const getSharedProjectsWithWriteAccess = () => {
    if (!permissions || !sharedProjects) return [];
    const sharedProjectIds = new Set([
      ...(permissions.projectWrite || [])
    ]);
    return sharedProjects.filter(project =>
      // Teams sharing: the project's editors array decides.
      sharingV2Doc(project)
        ? hasAccessToResource(permissions, project.id, 'write', 'project', project.owner || '', currentUser?.uid || '', project.design_firm_id, project)
        : sharedProjectIds.has(project.id));
  };

  // Render "Create New Project" option
  const renderCreateNewProjectOption = () => {
    return (
      <div>
        <div style={{ padding: '8px 16px' }}>
          <List sx={{ pt: 0, pb: 0 }}>
            <ListItem 
              onClick={handleCreateNewProject}
              sx={{ 
                cursor: 'pointer', 
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: '#f0f0f0' },
                py: 0,
                px: 3,
                minHeight: '32px',
              }}
            >
              <ListItemIcon sx={{ minWidth: '24px' }}>
                <AddIcon 
                  sx={{ 
                    fontSize: '18px', 
                    color: '#c58ed4' 
                  }} 
                />
              </ListItemIcon>
              <ListItemText 
                primary="Create New Project"
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#c58ed4'
                  }
                }}
              />
            </ListItem>
          </List>
        </div>
        <div style={{ 
          height: '1px', 
          backgroundColor: '#e0e0e0', 
          margin: '0px 16px' 
        }} />
      </div>
    );
  };

  // Render "None" option as a separate section
  const renderNoneOption = () => {
    return (
      <div>
        <div style={{ padding: '8px 16px' }}>
          <List sx={{ pt: 0, pb: 0 }}>
            <ListItem 
              onClick={() => handleSelectProject(null)}
              sx={{ 
                cursor: 'pointer', 
                backgroundColor: selectedProject === null ? '#f5f5f5' : 'transparent',
                '&:hover': { backgroundColor: '#f0f0f0' },
                py: 0,
                px: 3,
                minHeight: '32px',
              }}
            >
              <ListItemIcon sx={{ minWidth: '24px' }}>
                {selectedProject === null && (
                  <CheckIcon 
                    sx={{ 
                      fontSize: '18px', 
                      color: '#c58ed4' 
                    }} 
                  />
                )}
              </ListItemIcon>
              <ListItemText 
                primary="None"
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    fontSize: '14px',
                    fontStyle: 'italic',
                    color: '#666'
                  }
                }}
              />
            </ListItem>
          </List>
        </div>
        <div style={{ 
          height: '1px', 
          backgroundColor: '#e0e0e0', 
          margin: '0px 16px' 
        }} />
      </div>
    );
  };

  const renderProjectList = (projects: ProjectSchemeInterface[], sectionTitle: string, isLastSection = false) => {
    if (projects.length === 0) return null;
    
    return (
      <div>
        <Typography variant="h6" sx={{ p: 1, pl: 2, pb: 0, fontWeight: 'bold' }}>
          {sectionTitle}
        </Typography>
        <div style={{ maxHeight: '100px', overflowY: 'auto', padding: '0 8px' }}>
          <List sx={{ pt: 0, pb: 0 }}>
            {projects.map((project, index) => {
              const isSelected = selectedProject?.id === project.id;
              
              return (
                <ListItem 
                  key={`${sectionTitle}-${index}`}
                  onClick={() => handleSelectProject(project)}
                  sx={{ 
                    cursor: 'pointer', 
                    backgroundColor: isSelected ? '#f5f5f5' : 'transparent',
                    '&:hover': { backgroundColor: '#f0f0f0' },
                    py: 0,
                    px: 3,
                    minHeight: '32px',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '24px' }}>
                    {isSelected && (
                      <CheckIcon 
                        sx={{ 
                          fontSize: '18px', 
                          color: '#c58ed4' 
                        }} 
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText 
                    primary={project.name}
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        fontSize: '14px'
                      }
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </div>
        {!isLastSection && (
          <div style={{ 
            height: '1px', 
            backgroundColor: '#e0e0e0', 
            margin: '8px 16px' 
          }} />
        )}
      </div>
    );
  };
  
  return (
    <>
      {/* ProjectSchemeCreator Modal */}
      {showCreatorModal && (
        <ProjectSchemeCreator 
          project={{}} 
          setShowCreatorModal={setShowCreatorModal} 
          updateProject={handleProjectUpdate} 
        />
      )}
      
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
        PaperProps={{
          sx: {
            overflow: 'visible',
            ...(v2 ? { minHeight: '250px' } : { height: '250px' })
          }
        }}
        sx={{
          overflow: 'visible'
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Select Associated Project
        </DialogTitle>
        <DialogContent sx={{ overflow: 'visible', position: 'relative' }}>
          <Grid sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Associated Project
            </Typography>
            <Grid 
              sx={{ 
                position: 'relative',
                width: '100%'
              }}
              ref={dropdownRef}
            >
              <Grid
                onClick={handleToggleDropdown}
                data-testid="project-dropdown-trigger"
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  p: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: selectedProject ? 'black' : '#999'
                }}
              >
                <Typography noWrap sx={{ maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedProject 
                    ? selectedProject.name
                    : 'Select Project (Optional)'}
                </Typography>
                <KeyboardArrowDownIcon />
              </Grid>

              {showDropdown && (
                <Paper
                  elevation={3}
                  data-testid="project-dropdown"
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    width: '100%',
                    zIndex: 1300,
                    mt: 0.5,
                    pb: 1,
                  }}
                >
                  {renderCreateNewProjectOption()}
                  {renderNoneOption()}
                  {v2 ? renderProjectList(v2Projects(), "Projects", true) : <>
                    {renderProjectList(ownedProjects.filter(fileable), "My Projects", sharedProjects.length === 0)}
                    {renderProjectList(getSharedProjectsWithWriteAccess().filter(fileable), "Shared with Me", true)}
                  </>}
                </Paper>
              )}
            </Grid>
            {v2 && !ownsItem && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Only the owner can move this into a project.
              </Typography>
            )}
            {v2 && selectedProject && selectedProject.id !== initialProjectId && (
              <FilingWarning project={selectedProject} me={uid} then={`The ${kind} will be too.`} />
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'flex-start' }}>
          <ActionButton 
            variant="contained" 
            onClick={onClose}
          >
            Cancel
          </ActionButton>
          <ActionButton 
            variant="contained" 
            onClick={handleSave}
            disabled={saving || (selectedProject?.id === initialProjectId)}
          >
            {saving ? "Saving" : "Save"}
          </ActionButton>
        </DialogActions>
      </Dialog>
    </>
  );
};